<rfid-access-log>
  <div class="rfid-access-log">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="bi bi-clock-history me-2"></i>
          Historial de Accesos RFID
          <span if={state.realtimeEnabled} class="badge bg-success ms-2">
            <i class="bi bi-broadcast"></i>
            En vivo
          </span>
        </h5>
        <button class="btn btn-sm btn-outline-danger app-btn app-btn-sm" onclick={clearLog}>
          <i class="bi bi-trash me-1"></i>
          Limpiar
        </button>
      </div>

      <div class="card-body">
        <!-- Filtros -->
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label">Filtrar por Estado</label>
            <select class="form-select" value={state.statusFilter} onchange={handleStatusFilter}>
              <option value="all">Todos</option>
              <option value="granted">Acceso Permitido</option>
              <option value="denied">Acceso Denegado</option>
              <option value="error">Errores</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Total de Eventos</label>
            <input type="text" class="form-control" readonly value={state.filteredEvents.length} />
          </div>
        </div>

        <!-- Lista de eventos -->
        <div if={state.loading} class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="text-muted mt-3">Cargando historial...</p>
        </div>

        <div if={state.error && !state.loading} class="alert alert-danger" role="alert">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {state.error}
        </div>

        <div if={state.filteredEvents.length > 0 && !state.loading} class="table-responsive" style="max-height: 500px; overflow-y: auto;">
          <table class="table table-sm table-hover">
            <thead class="sticky-top bg-white">
              <tr>
                <th style="width: 140px;">Fecha/Hora</th>
                <th>Dispositivo</th>
                <th>Tarjeta</th>
                <th>Usuario</th>
                <th>Estado</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              <tr each={event in state.filteredEvents} key={event.id} class={'table-' + getRowClass(event.status)}>
                <td>
                  <small>{formatDateTime(event.timestamp)}</small>
                </td>
                <td>
                  <strong>{event.deviceName || event.deviceId}</strong>
                </td>
                <td>
                  <code class="small">{event.cardNumber}</code>
                  <br/>
                  <small class="text-muted">{event.userNumber || '-'}</small>
                </td>
                <td>
                  <small>{event.userName || '-'}</small>
                </td>
                <td>
                  <span class={'badge ' + getStatusBadge(event.status)}>
                    <i class={'bi bi-' + getStatusIcon(event.status) + ' me-1'}></i>
                    {getStatusText(event.status)}
                  </span>
                </td>
                <td>
                  <small class="text-muted">{event.reason || '-'}</small>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty state -->
        <div if={state.filteredEvents.length === 0 && !state.loading && !state.error} class="text-center py-5">
          <i class="bi bi-clock-history text-muted" style="font-size: 3rem;"></i>
          <p class="text-muted mt-3">
            {state.statusFilter !== 'all'
              ? 'No hay eventos con ese filtro'
              : 'No hay eventos de acceso RFID aún'}
          </p>
          <p if={state.realtimeEnabled} class="text-success">
            <i class="bi bi-broadcast-pin me-1"></i>
            Esperando eventos en tiempo real...
          </p>
        </div>
      </div>
    </div>
  </div>

  <script>
    import { webSocketService } from '../services/WebSocketService';
    import { RfidCardService } from '../services/api/rfidCard.service';

    export default {
      onBeforeMount(props) {
        this.state = {
          events: [],
          filteredEvents: [],
          statusFilter: 'all',
          realtimeEnabled: false,
          loading: true,
          error: null
        };

        this.maxEvents = 100; // Máximo de eventos en memoria
      },

      async onMounted() {
        // Load historical data from database
        await this.loadHistoryFromDB();

        // Suscribirse a eventos WebSocket
        this.handleRfidAccessEvent = this.handleRfidAccessEvent.bind(this);
        webSocketService.on('rfidAccess', this.handleRfidAccessEvent);

        // Verificar si WebSocket está conectado
        this.update({ realtimeEnabled: webSocketService.isConnected() });

        // Escuchar cambios de conexión
        this.handleConnectionStatus = (status) => {
          this.update({ realtimeEnabled: status === 'connected' });
        };
        webSocketService.on('connectionStatus', this.handleConnectionStatus);
      },

      async loadHistoryFromDB() {
        if (!this.props.userId) {
          this.update({ loading: false });
          return;
        }

        try {
          this.update({ loading: true, error: null });

          const response = await RfidCardService.getRfidHistory(this.props.userId, {
            limit: this.maxEvents,
            skip: 0
          });

          console.log('📊 [rfid-access-log] Raw response from API:', response);

          // Convert DB entries to the format expected by the UI
          const events = response.data.map(entry => {
            const parsedData = entry.parsedData || {};

            console.log('🔍 [rfid-access-log] Processing entry:', {
              id: entry._id,
              device: entry.device,
              parsedData,
              hasDevice: !!entry.device,
              status: parsedData.status
            });

            // Determine status - default to 'error' if not found
            let status = 'error';
            if (parsedData.status) {
              status = parsedData.status;
            } else if (entry.type === 'error') {
              status = 'error';
            }

            return {
              id: entry._id,
              timestamp: entry.date,
              deviceId: entry.device?._id || 'unknown',
              deviceName: entry.device?.name || 'Dispositivo desconocido',
              cardNumber: parsedData.cardNumber || 'N/A',
              userNumber: parsedData.userNumber || '-',
              userName: parsedData.userName || '-',
              status: status,
              reason: parsedData.reason || parsedData.error || '-',
              channel: parsedData.channel
            };
          });

          this.update({
            events,
            loading: false
          });
          this.applyFilter();

          console.log(`📊 Loaded ${events.length} RFID history entries from database`);
        } catch (error) {
          console.error('Error loading RFID history:', error);
          this.update({
            error: 'Error al cargar el historial',
            loading: false
          });
        }
      },

      onBeforeUnmount() {
        // Desuscribirse
        webSocketService.off('rfidAccess', this.handleRfidAccessEvent);
        webSocketService.off('connectionStatus', this.handleConnectionStatus);
      },

      handleRfidAccessEvent(data) {
        console.log('RFID Access Event:', data);

        // Filtrar por userId o deviceId si se especificó
        if (this.props.userId && data.userId !== this.props.userId) {
          return; // Ignorar eventos de otros usuarios
        }

        if (this.props.deviceId && data.deviceId !== this.props.deviceId) {
          return; // Ignorar eventos de otros dispositivos
        }

        const event = {
          id: Date.now() + Math.random(), // ID único
          timestamp: data.timestamp || Date.now(),
          deviceId: data.deviceId,
          deviceName: data.deviceName,
          cardNumber: data.cardNumber,
          userNumber: data.userNumber,
          userName: data.userName,
          status: data.status, // granted, denied, error
          reason: data.reason,
          channel: data.channel
        };

        // Añadir al principio de la lista
        const events = [event, ...this.state.events];

        // Limitar a maxEvents
        if (events.length > this.maxEvents) {
          events.length = this.maxEvents;
        }

        this.update({ events });
        this.applyFilter();
      },

      handleStatusFilter(e) {
        this.update({ statusFilter: e.target.value });
        this.applyFilter();
      },

      applyFilter() {
        const { events, statusFilter } = this.state;

        let filteredEvents = events;

        if (statusFilter !== 'all') {
          filteredEvents = events.filter(e => e.status === statusFilter);
        }

        this.update({ filteredEvents });
      },

      clearLog() {
        if (confirm('¿Estás seguro de limpiar el historial?')) {
          this.update({
            events: [],
            filteredEvents: []
          });
        }
      },

      getRowClass(status) {
        switch(status) {
          case 'granted': return 'success';
          case 'denied': return 'warning';
          case 'error': return 'danger';
          default: return '';
        }
      },

      getStatusBadge(status) {
        switch(status) {
          case 'granted': return 'bg-success';
          case 'denied': return 'bg-warning text-dark';
          case 'error': return 'bg-danger';
          default: return 'bg-secondary';
        }
      },

      getStatusIcon(status) {
        switch(status) {
          case 'granted': return 'check-circle-fill';
          case 'denied': return 'exclamation-triangle-fill';
          case 'error': return 'x-circle-fill';
          default: return 'question-circle-fill';
        }
      },

      getStatusText(status) {
        switch(status) {
          case 'granted': return 'Permitido';
          case 'denied': return 'Denegado';
          case 'error': return 'Error';
          default: return 'Desconocido';
        }
      },

      formatDateTime(timestamp) {
        const date = new Date(timestamp);
        return date.toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      }
    };
  </script>

  <style>
    .rfid-access-log .table thead {
      position: sticky;
      top: 0;
      z-index: 10;
    }

    .rfid-access-log code {
      background-color: #f8f9fa;
      padding: 0.1rem 0.3rem;
      border-radius: 0.2rem;
      font-size: 0.85em;
    }
  </style>
</rfid-access-log>
